import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
// import { usePermissions } from '../hooks/usePermissions'; // Pour future utilisation
import { API } from '../services/api';
import appConfig from '../config';
import ImageCropper from '../components/ui/ImageCropper';
import GlobalHeader from '../components/GlobalHeader';
import SharedMailboxesTab from '../components/directory/SharedMailboxesTab';
import './DirectorySettingsPage.css';

// Tabs disponibles
const VALID_TABS = ['config', 'groups', 'users', 'shared', 'logs'];

function DirectorySettingsPage() {
    const location = useLocation();
    const navigate = useNavigate();

    // Lire le tab depuis l'URL hash
    const getTabFromHash = useCallback(() => {
        const hash = location.hash.replace('#', '');
        return VALID_TABS.includes(hash) ? hash : 'config';
    }, [location.hash]);

    const [activeTab, setActiveTab] = useState(getTabFromHash);

    // Mettre à jour le tab quand l'URL change
    useEffect(() => {
        setActiveTab(getTabFromHash());
    }, [getTabFromHash]);

    // Fonction pour changer de tab et mettre à jour l'URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`#${tab}`, { replace: true });
    };
    const { loading: authLoading, user: authUser } = useAuth();
    const toast = useToast();
    // const { canManage } = usePermissions(); // Pour future utilisation

    // Config state - multi-config
    const [configs, setConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [isNewConfig, setIsNewConfig] = useState(false);
    const emptyConfigForm = {
        name: '',
        directory_type: 'mock',
        ldap_server: '',
        ldap_port: 389,
        ldap_use_ssl: false,
        ldap_bind_dn: '',
        ldap_bind_password: '',
        ldap_base_dn: '',
        ldap_user_filter: '(objectClass=inetOrgPerson)',
        ldap_group_filter: '(objectClass=groupOfNames)',
        azure_tenant_id: '',
        azure_client_id: '',
        azure_client_secret: '',
        auto_sync_enabled: false,
        auto_sync_interval: 3600
    };
    const [configForm, setConfigForm] = useState(emptyConfigForm);

    // Filtre source pour onglets groupes/users/logs
    const [sourceFilter, setSourceFilter] = useState(null);

    // Filtres par défaut selon le type d'annuaire
    const getDefaultFilters = (type) => {
        switch (type) {
            case 'ldap':
                return {
                    ldap_user_filter: '(objectClass=inetOrgPerson)',
                    ldap_group_filter: '(objectClass=groupOfNames)',
                    ldap_port: 389
                };
            case 'ad_onpremise':
                return {
                    ldap_user_filter: '(&(objectClass=user)(objectCategory=person))',
                    ldap_group_filter: '(objectClass=group)',
                    ldap_port: 389
                };
            default:
                return {};
        }
    };

    // Gérer le changement de type d'annuaire
    const handleDirectoryTypeChange = (newType) => {
        const defaults = getDefaultFilters(newType);
        setConfigForm(prev => ({
            ...prev,
            directory_type: newType,
            ...defaults
        }));
    };

    // Data state
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [signatures, setSignatures] = useState([]);

    // UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [testing, setTesting] = useState(false);

    // Assign signature modal (for groups)
    const [assignModal, setAssignModal] = useState({ open: false, groupId: null, groupName: '' });
    const [selectedSignature, setSelectedSignature] = useState('');

    // Assign signature modal (for users)
    const [userAssignModal, setUserAssignModal] = useState({ open: false, userId: null, userName: '' });
    const [selectedUserSignature, setSelectedUserSignature] = useState('');

    // Expanded user (to show assigned signatures)
    const [expandedUserId, setExpandedUserId] = useState(null);

    // User personalization modal (greeting, profile photo)
    const [personalizationModal, setPersonalizationModal] = useState({
        open: false,
        user: null,
        greeting: '',
        profilePhoto: '',
        uploading: false
    });

    // Image cropper state
    const [cropperModal, setCropperModal] = useState({
        open: false,
        imageSrc: null,
        imageType: 'image/jpeg'
    });

    // Preview effective signature modal
    const [previewModal, setPreviewModal] = useState({
        open: false,
        user: null,
        loading: false,
        effectiveSignatures: [],
        selectedDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [configsRes, groupsRes, usersRes, logsRes, signaturesRes] = await Promise.all([
                API.getDirectoryConfigs(),
                API.getDirectoryGroups(sourceFilter),
                API.getDirectoryUsers(sourceFilter),
                API.getDirectoryLogs(20, 0, sourceFilter),
                API.getSignatures()
            ]);

            if (configsRes?.data?.configs) {
                setConfigs(configsRes.data.configs);
                // Sélectionner la première config si aucune n'est sélectionnée
                if (!selectedConfigId && configsRes.data.configs.length > 0 && !isNewConfig) {
                    const firstConfig = configsRes.data.configs[0];
                    setSelectedConfigId(firstConfig.id);
                    setConfigForm({ ...emptyConfigForm, ...firstConfig });
                }
            }
            if (groupsRes?.data?.groups) setGroups(groupsRes.data.groups);
            if (usersRes?.data?.users) setUsers(usersRes.data.users);
            if (logsRes?.data?.logs) setLogs(logsRes.data.logs);
            if (signaturesRes?.data?.signatures) setSignatures(signaturesRes.data.signatures);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // Recharger groupes/users/logs quand le filtre source change
    useEffect(() => {
        if (!loading) {
            const reloadFiltered = async () => {
                try {
                    const [groupsRes, usersRes, logsRes] = await Promise.all([
                        API.getDirectoryGroups(sourceFilter),
                        API.getDirectoryUsers(sourceFilter),
                        API.getDirectoryLogs(20, 0, sourceFilter)
                    ]);
                    if (groupsRes?.data?.groups) setGroups(groupsRes.data.groups);
                    if (usersRes?.data?.users) setUsers(usersRes.data.users);
                    if (logsRes?.data?.logs) setLogs(logsRes.data.logs);
                } catch (error) {
                    console.error('Error reloading filtered data:', error);
                }
            };
            reloadFiltered();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceFilter]);

    const handleSelectConfig = (config) => {
        setIsNewConfig(false);
        setSelectedConfigId(config.id);
        setConfigForm({ ...emptyConfigForm, ...config });
    };

    const handleNewConfig = () => {
        setIsNewConfig(true);
        setSelectedConfigId(null);
        setConfigForm(emptyConfigForm);
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (isNewConfig) {
                const result = await API.createDirectoryConfig(configForm);
                toast.success('Configuration créée avec succès');
                setIsNewConfig(false);
                setSelectedConfigId(result.data.config_id);
            } else if (selectedConfigId) {
                await API.updateDirectoryConfig(selectedConfigId, configForm);
                toast.success('Configuration mise à jour');
            }
            loadData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!selectedConfigId) return;
        const config = configs.find(c => c.id === selectedConfigId);
        if (!window.confirm(`Supprimer la configuration "${config?.name}" ?\nLes utilisateurs et groupes associés seront détachés mais conservés.`)) return;
        try {
            const result = await API.deleteDirectoryConfig(selectedConfigId);
            toast.success(`Configuration supprimée. ${result.data.detached_users} utilisateur(s) et ${result.data.detached_groups} groupe(s) détachés.`);
            setSelectedConfigId(null);
            setIsNewConfig(false);
            setConfigForm(emptyConfigForm);
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleTestConnection = async () => {
        if (!selectedConfigId) return;
        try {
            setTesting(true);
            await API.testDirectoryConnection(selectedConfigId);
            toast.success('Connexion réussie !');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setTesting(false);
        }
    };

    const handleSync = async () => {
        if (!selectedConfigId) return;
        try {
            setSyncing(true);
            const result = await API.syncDirectory(selectedConfigId);
            toast.success(result.data.message || 'Synchronisation terminée');
            loadData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSyncing(false);
        }
    };

    const getDirectoryTypeLabel = (type) => {
        const labels = {
            'ldap': 'LDAP',
            'ad_onpremise': 'AD On-Premise',
            'azure_ad': 'Azure AD',
            'mock': 'Test'
        };
        return labels[type] || type;
    };

    const getSyncStatusBadge = (status) => {
        if (!status) return null;
        const classes = {
            'success': 'badge-success',
            'partial': 'badge-warning',
            'failed': 'badge-danger'
        };
        return <span className={`badge ${classes[status] || ''}`}>{status}</span>;
    };

    const handleAssignSignature = async () => {
        if (!selectedSignature || !assignModal.groupId) return;
        try {
            await API.assignSignatureToGroup(assignModal.groupId, selectedSignature);
            toast.success('Signature assignée avec succès');
            setAssignModal({ open: false, groupId: null, groupName: '' });
            setSelectedSignature('');
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleRemoveSignature = async (groupId, signatureId) => {
        if (!window.confirm('Retirer cette signature du groupe ?')) return;
        try {
            await API.removeSignatureFromGroup(groupId, signatureId);
            toast.success('Signature retirée du groupe');
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    // User signature assignment handlers
    const handleAssignSignatureToUser = async () => {
        if (!selectedUserSignature || !userAssignModal.userId) return;
        try {
            await API.assignSignatureToUser(userAssignModal.userId, selectedUserSignature);
            toast.success('Signature assignée à l\'utilisateur');
            setUserAssignModal({ open: false, userId: null, userName: '' });
            setSelectedUserSignature('');
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleRemoveSignatureFromUser = async (userId, signatureId) => {
        if (!window.confirm('Retirer cette signature de l\'utilisateur ?')) return;
        try {
            await API.removeSignatureFromUser(userId, signatureId);
            toast.success('Signature retirée de l\'utilisateur');
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleUpdateGroupPriority = async (groupId, priority) => {
        try {
            await API.updateGroupPriority(groupId, priority);
            toast.success('Priorité mise à jour');
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const toggleUserExpand = (userId) => {
        setExpandedUserId(expandedUserId === userId ? null : userId);
    };

    // User personalization handlers
    const openPersonalizationModal = (u) => {
        setPersonalizationModal({
            open: true,
            user: u,
            greeting: u.greeting || '',
            profilePhoto: u.profile_photo || '',
            uploading: false
        });
    };

    const closePersonalizationModal = () => {
        setPersonalizationModal({
            open: false,
            user: null,
            greeting: '',
            profilePhoto: '',
            uploading: false
        });
    };

    const handleSavePersonalization = async () => {
        if (!personalizationModal.user) return;
        try {
            await API.updateUserPersonalization(personalizationModal.user.id, {
                greeting: personalizationModal.greeting
            });
            toast.success('Personnalisation mise à jour');
            closePersonalizationModal();
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleProfilePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (!file || !personalizationModal.user) return;

        // Vérifier que c'est une image
        if (!file.type.startsWith('image/')) {
            toast.error('Veuillez sélectionner une image');
            return;
        }

        // Déterminer le format de sortie (conserver PNG pour la transparence)
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

        // Lire le fichier et ouvrir le cropper
        const reader = new FileReader();
        reader.onload = () => {
            setCropperModal({
                open: true,
                imageSrc: reader.result,
                imageType: outputType
            });
        };
        reader.readAsDataURL(file);

        // Reset l'input pour permettre de resélectionner le même fichier
        e.target.value = '';
    };

    const handleCropComplete = async (croppedBlob) => {
        const imageType = cropperModal.imageType;
        setCropperModal({ open: false, imageSrc: null, imageType: 'image/jpeg' });

        if (!personalizationModal.user) return;

        setPersonalizationModal(prev => ({ ...prev, uploading: true }));
        try {
            // Créer un File à partir du Blob (avec la bonne extension)
            const extension = imageType === 'image/png' ? 'png' : 'jpg';
            const file = new File([croppedBlob], `profile.${extension}`, { type: imageType });
            const result = await API.uploadUserProfilePhoto(personalizationModal.user.id, file);
            setPersonalizationModal(prev => ({
                ...prev,
                profilePhoto: result.data.profile_photo,
                uploading: false
            }));
            toast.success('Photo uploadée avec succès');
            loadData();
        } catch (error) {
            toast.error(error.message);
            setPersonalizationModal(prev => ({ ...prev, uploading: false }));
        }
    };

    const handleCropCancel = () => {
        setCropperModal({ open: false, imageSrc: null });
    };

    // Preview effective signature handlers
    const openPreviewModal = async (user) => {
        setPreviewModal({
            open: true,
            user,
            loading: true,
            effectiveSignatures: [],
            selectedDate: new Date().toISOString().split('T')[0]
        });
        await loadEffectiveSignatures(user.email, new Date().toISOString().split('T')[0]);
    };

    const loadEffectiveSignatures = async (email, date) => {
        try {
            setPreviewModal(prev => ({ ...prev, loading: true }));
            const orgId = authUser?.organization_id || 1; // Utiliser l'org de l'utilisateur connecté
            const result = await API.getUserEffectiveSignatures(email, orgId, date);
            // L'API retourne valid_signatures (triées par priorité) et all_signatures
            const validSigs = result?.data?.valid_signatures || [];
            const allSigs = result?.data?.all_signatures || [];
            // Afficher les signatures valides en premier, puis les autres
            const displaySigs = validSigs.length > 0 ? validSigs : allSigs;
            setPreviewModal(prev => ({
                ...prev,
                loading: false,
                effectiveSignatures: displaySigs
            }));
        } catch (error) {
            console.error('Error loading effective signatures:', error);
            setPreviewModal(prev => ({
                ...prev,
                loading: false,
                effectiveSignatures: []
            }));
            toast.error(error.message);
        }
    };

    const handlePreviewDateChange = async (newDate) => {
        setPreviewModal(prev => ({ ...prev, selectedDate: newDate }));
        if (previewModal.user) {
            await loadEffectiveSignatures(previewModal.user.email, newDate);
        }
    };

    const closePreviewModal = () => {
        setPreviewModal({
            open: false,
            user: null,
            loading: false,
            effectiveSignatures: [],
            selectedDate: new Date().toISOString().split('T')[0]
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('fr-FR');
    };

    if (authLoading) {
        return (
            <>
                <GlobalHeader />
                <div className="directory-settings">
                    <div className="loading">Chargement...</div>
                </div>
            </>
        );
    }

    return (
        <>
            <GlobalHeader />
            <div className="directory-settings">
                <div className="settings-header">
                    <h1>Intégration Annuaire</h1>
                    <p className="subtitle">Synchronisez vos utilisateurs et groupes depuis LDAP ou Azure AD</p>
                </div>

                <div className="settings-tabs">
                    <button
                        className={`tab ${activeTab === 'config' ? 'active' : ''}`}
                        onClick={() => handleTabChange('config')}
                    >
                        Configuration
                    </button>
                    <button
                        className={`tab ${activeTab === 'groups' ? 'active' : ''}`}
                        onClick={() => handleTabChange('groups')}
                    >
                        Groupes ({groups.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => handleTabChange('users')}
                    >
                        Employés ({users.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'shared' ? 'active' : ''}`}
                        onClick={() => handleTabChange('shared')}
                    >
                        Boîtes partagées
                    </button>
                    <button
                        className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
                        onClick={() => handleTabChange('logs')}
                    >
                        Historique
                    </button>
                </div>

                <div className="settings-content">
                    {loading ? (
                        <div className="loading">Chargement...</div>
                    ) : (
                        <>
                            {/* Configuration Tab - Layout Liste + Détail */}
                            {activeTab === 'config' && (
                                <div className="config-tab config-multi-layout">
                                    {/* Panneau gauche : liste des configs */}
                                    <div className="config-list-panel">
                                        <button className="btn-add-config" onClick={handleNewConfig}>
                                            + Ajouter un annuaire
                                        </button>
                                        {configs.length === 0 && !isNewConfig && (
                                            <p className="no-configs">Aucune configuration. Ajoutez un annuaire pour commencer.</p>
                                        )}
                                        {configs.map(c => (
                                            <div
                                                key={c.id}
                                                className={`config-list-item ${selectedConfigId === c.id && !isNewConfig ? 'selected' : ''}`}
                                                onClick={() => handleSelectConfig(c)}
                                            >
                                                <div className="config-item-name">{c.name}</div>
                                                <span className={`config-type-badge ${c.directory_type}`}>
                                                    {getDirectoryTypeLabel(c.directory_type)}
                                                </span>
                                                {c.last_sync_status && getSyncStatusBadge(c.last_sync_status)}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Panneau droit : formulaire de la config sélectionnée */}
                                    <div className="config-detail-panel">
                                        {!selectedConfigId && !isNewConfig ? (
                                            <div className="config-empty-state">
                                                <p>Sélectionnez une configuration ou ajoutez-en une nouvelle.</p>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSaveConfig}>
                                                {/* Nom de la config */}
                                                <div className="form-section">
                                                    <h3>{isNewConfig ? 'Nouvelle configuration' : 'Modifier la configuration'}</h3>
                                                    <div className="form-group">
                                                        <label>Nom de la configuration</label>
                                                        <input
                                                            type="text"
                                                            value={configForm.name}
                                                            onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                                                            placeholder="Ex: AD Paris, Azure Cloud, Office 365..."
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-section">
                                                    <h3>Type d'annuaire</h3>
                                                    <div className="directory-type-selector">
                                                        <label className={`type-option ${configForm.directory_type === 'mock' ? 'selected' : ''}`}>
                                                            <input type="radio" name="directory_type" value="mock" checked={configForm.directory_type === 'mock'} onChange={() => handleDirectoryTypeChange('mock')} />
                                                            <span className="type-icon">🧪</span>
                                                            <span className="type-name">Mode Test</span>
                                                            <span className="type-desc">Données simulées</span>
                                                        </label>
                                                        <label className={`type-option ${configForm.directory_type === 'ldap' ? 'selected' : ''}`}>
                                                            <input type="radio" name="directory_type" value="ldap" checked={configForm.directory_type === 'ldap'} onChange={() => handleDirectoryTypeChange('ldap')} />
                                                            <span className="type-icon">📁</span>
                                                            <span className="type-name">LDAP</span>
                                                            <span className="type-desc">OpenLDAP standard</span>
                                                        </label>
                                                        <label className={`type-option ${configForm.directory_type === 'ad_onpremise' ? 'selected' : ''}`}>
                                                            <input type="radio" name="directory_type" value="ad_onpremise" checked={configForm.directory_type === 'ad_onpremise'} onChange={() => handleDirectoryTypeChange('ad_onpremise')} />
                                                            <span className="type-icon">🖥️</span>
                                                            <span className="type-name">Microsoft AD</span>
                                                            <span className="type-desc">AD on-premise</span>
                                                        </label>
                                                        <label className={`type-option ${configForm.directory_type === 'azure_ad' ? 'selected' : ''}`}>
                                                            <input type="radio" name="directory_type" value="azure_ad" checked={configForm.directory_type === 'azure_ad'} onChange={() => handleDirectoryTypeChange('azure_ad')} />
                                                            <span className="type-icon">☁️</span>
                                                            <span className="type-name">Microsoft 365</span>
                                                            <span className="type-desc">Azure AD / Entra ID</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* LDAP / AD On-Premise Configuration */}
                                                {(configForm.directory_type === 'ldap' || configForm.directory_type === 'ad_onpremise') && (
                                                    <div className="form-section">
                                                        <h3>{configForm.directory_type === 'ad_onpremise' ? 'Configuration Active Directory' : 'Configuration LDAP'}</h3>
                                                        <div className="form-grid">
                                                            <div className="form-group">
                                                                <label>{configForm.directory_type === 'ad_onpremise' ? 'Serveur AD (nom ou IP)' : 'Serveur LDAP'}</label>
                                                                <input type="text" value={configForm.ldap_server} onChange={(e) => setConfigForm({ ...configForm, ldap_server: e.target.value })} placeholder={configForm.directory_type === 'ad_onpremise' ? 'ad.entreprise.local' : 'ldap.example.com'} />
                                                            </div>
                                                            <div className="form-group">
                                                                <label>Port</label>
                                                                <input type="number" value={configForm.ldap_port} onChange={(e) => setConfigForm({ ...configForm, ldap_port: parseInt(e.target.value) })} />
                                                                <span className="field-hint">389 (standard) ou 636 (SSL)</span>
                                                            </div>
                                                            <div className="form-group checkbox-group">
                                                                <label>
                                                                    <input type="checkbox" checked={configForm.ldap_use_ssl} onChange={(e) => setConfigForm({ ...configForm, ldap_use_ssl: e.target.checked })} />
                                                                    Utiliser SSL/TLS (LDAPS)
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="form-group">
                                                            <label>{configForm.directory_type === 'ad_onpremise' ? 'Compte de service (Bind DN)' : 'Bind DN'}</label>
                                                            <input type="text" value={configForm.ldap_bind_dn} onChange={(e) => setConfigForm({ ...configForm, ldap_bind_dn: e.target.value })} placeholder={configForm.directory_type === 'ad_onpremise' ? 'CN=Service,OU=Services,DC=entreprise,DC=local' : 'cn=admin,dc=example,dc=com'} />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Mot de passe</label>
                                                            <input type="password" value={configForm.ldap_bind_password} onChange={(e) => setConfigForm({ ...configForm, ldap_bind_password: e.target.value })} placeholder="••••••••" />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Base DN (racine de recherche)</label>
                                                            <input type="text" value={configForm.ldap_base_dn} onChange={(e) => setConfigForm({ ...configForm, ldap_base_dn: e.target.value })} placeholder={configForm.directory_type === 'ad_onpremise' ? 'DC=entreprise,DC=local' : 'dc=example,dc=com'} />
                                                        </div>
                                                        <div className="form-grid">
                                                            <div className="form-group">
                                                                <label>Filtre utilisateurs</label>
                                                                <input type="text" value={configForm.ldap_user_filter} onChange={(e) => setConfigForm({ ...configForm, ldap_user_filter: e.target.value })} />
                                                            </div>
                                                            <div className="form-group">
                                                                <label>Filtre groupes</label>
                                                                <input type="text" value={configForm.ldap_group_filter} onChange={(e) => setConfigForm({ ...configForm, ldap_group_filter: e.target.value })} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Azure AD Configuration */}
                                                {configForm.directory_type === 'azure_ad' && (
                                                    <div className="form-section">
                                                        <h3>Configuration Azure AD</h3>
                                                        <div className="form-group">
                                                            <label>Tenant ID</label>
                                                            <input type="text" value={configForm.azure_tenant_id} onChange={(e) => setConfigForm({ ...configForm, azure_tenant_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Client ID (Application ID)</label>
                                                            <input type="text" value={configForm.azure_client_id} onChange={(e) => setConfigForm({ ...configForm, azure_client_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Client Secret</label>
                                                            <input type="password" value={configForm.azure_client_secret} onChange={(e) => setConfigForm({ ...configForm, azure_client_secret: e.target.value })} placeholder="••••••••" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Mock info */}
                                                {configForm.directory_type === 'mock' && (
                                                    <div className="form-section">
                                                        <div className="info-box">
                                                            <h4>Mode Test</h4>
                                                            <p>Ce mode utilise des données simulées pour tester l'intégration.</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sync Settings */}
                                                <div className="form-section">
                                                    <h3>Synchronisation</h3>
                                                    <div className="sync-mode-selector">
                                                        <label className={`sync-option ${!configForm.auto_sync_enabled ? 'selected' : ''}`}>
                                                            <input type="radio" name="sync_mode" checked={!configForm.auto_sync_enabled} onChange={() => setConfigForm({ ...configForm, auto_sync_enabled: false })} />
                                                            <span className="sync-icon">👆</span>
                                                            <div className="sync-info">
                                                                <span className="sync-name">Manuelle</span>
                                                                <span className="sync-desc">Lancement manuel</span>
                                                            </div>
                                                        </label>
                                                        <label className={`sync-option ${configForm.auto_sync_enabled ? 'selected' : ''}`}>
                                                            <input type="radio" name="sync_mode" checked={configForm.auto_sync_enabled} onChange={() => setConfigForm({ ...configForm, auto_sync_enabled: true })} />
                                                            <span className="sync-icon">🔄</span>
                                                            <div className="sync-info">
                                                                <span className="sync-name">Automatique</span>
                                                                <span className="sync-desc">Synchronisation périodique</span>
                                                            </div>
                                                        </label>
                                                    </div>
                                                    {configForm.auto_sync_enabled && (
                                                        <div className="sync-frequency-section">
                                                            <div className="form-group">
                                                                <label>Fréquence</label>
                                                                <select value={configForm.auto_sync_interval} onChange={(e) => setConfigForm({ ...configForm, auto_sync_interval: parseInt(e.target.value) })}>
                                                                    <option value={1800}>Toutes les 30 minutes</option>
                                                                    <option value={3600}>Toutes les heures</option>
                                                                    <option value={7200}>Toutes les 2 heures</option>
                                                                    <option value={21600}>Toutes les 6 heures</option>
                                                                    <option value={43200}>Toutes les 12 heures</option>
                                                                    <option value={86400}>Tous les jours</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="form-actions">
                                                    <button type="submit" className="btn-primary" disabled={saving}>
                                                        {saving ? 'Enregistrement...' : (isNewConfig ? 'Créer la configuration' : 'Enregistrer')}
                                                    </button>
                                                    {!isNewConfig && selectedConfigId && (
                                                        <>
                                                            <button type="button" className="btn-secondary" onClick={handleTestConnection} disabled={testing}>
                                                                {testing ? 'Test en cours...' : 'Tester la connexion'}
                                                            </button>
                                                            <button type="button" className="btn-sync" onClick={handleSync} disabled={syncing}>
                                                                {syncing ? 'Synchronisation...' : '🔄 Synchroniser'}
                                                            </button>
                                                            <button type="button" className="btn-danger" onClick={handleDeleteConfig}>
                                                                Supprimer
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </form>
                                        )}

                                        {/* Sync status pour la config sélectionnée */}
                                        {!isNewConfig && selectedConfigId && (() => {
                                            const selectedConfig = configs.find(c => c.id === selectedConfigId);
                                            return selectedConfig ? (
                                                <div className="config-status">
                                                    <h4>État de la synchronisation</h4>
                                                    <p><strong>Dernière sync:</strong> {formatDate(selectedConfig.last_sync_at)}</p>
                                                    <p><strong>Statut:</strong> <span className={`status-badge ${selectedConfig.last_sync_status || 'none'}`}>
                                                        {selectedConfig.last_sync_status === 'success' ? 'Succès' :
                                                         selectedConfig.last_sync_status === 'failed' ? 'Échec' :
                                                         selectedConfig.last_sync_status === 'partial' ? 'Partiel' : 'Jamais synchronisé'}
                                                    </span></p>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* Groups Tab */}
                            {activeTab === 'groups' && (
                                <div className="groups-tab">
                                    {configs.length > 1 && (
                                        <div className="source-filter">
                                            <label>Filtrer par source :</label>
                                            <select value={sourceFilter || ''} onChange={(e) => setSourceFilter(e.target.value ? parseInt(e.target.value) : null)}>
                                                <option value="">Tous les annuaires</option>
                                                {configs.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({getDirectoryTypeLabel(c.directory_type)})</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="tab-info">
                                        <p>La priorité détermine quelle signature est utilisée quand un utilisateur appartient à plusieurs groupes. Plus la valeur est élevée, plus le groupe est prioritaire (1-100).</p>
                                    </div>
                                    {groups.length === 0 ? (
                                        <div className="empty-state">
                                            <p>Aucun groupe synchronisé</p>
                                            <p className="hint">Configurez l'annuaire et lancez une synchronisation</p>
                                        </div>
                                    ) : (
                                        <div className="groups-list">
                                            {groups.map(group => (
                                                <div key={group.id} className="group-card">
                                                    <div className="group-header">
                                                        <div className="group-title">
                                                            <h4>{group.name}</h4>
                                                            {group.config_name && <span className="source-badge">{group.config_name}</span>}
                                                            {!group.directory_config_id && <span className="source-badge orphan">Non rattaché</span>}
                                                            <span className="member-count">{group.actual_member_count} membres</span>
                                                        </div>
                                                        <div className="group-actions">
                                                            <button
                                                                className="btn-calendar"
                                                                onClick={() => navigate(`/signatures/calendar?group=${group.id}`)}
                                                                title="Voir le calendrier des signatures"
                                                            >
                                                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                Calendrier
                                                            </button>
                                                            <div className="group-priority">
                                                                <label>Priorité:</label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="100"
                                                                    value={group.group_priority || 50}
                                                                    onChange={(e) => {
                                                                        const newPriority = parseInt(e.target.value) || 50;
                                                                        if (newPriority >= 1 && newPriority <= 100) {
                                                                            handleUpdateGroupPriority(group.id, newPriority);
                                                                        }
                                                                    }}
                                                                    className="priority-input"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {group.description && <p className="group-desc">{group.description}</p>}

                                                    <div className="assigned-signatures">
                                                        <h5>Signatures assignées:</h5>
                                                        {group.assigned_signatures?.length > 0 ? (
                                                            <ul>
                                                                {group.assigned_signatures.map(sig => (
                                                                    <li key={sig.id}>
                                                                        {sig.name}
                                                                        <button
                                                                            className="btn-remove"
                                                                            onClick={() => handleRemoveSignature(group.id, sig.id)}
                                                                            title="Retirer"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="no-signatures">Aucune signature</p>
                                                        )}
                                                        <button
                                                            className="btn-add-signature"
                                                            onClick={() => setAssignModal({ open: true, groupId: group.id, groupName: group.name })}
                                                        >
                                                            + Assigner une signature
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Users Tab */}
                            {activeTab === 'users' && (
                                <div className="users-tab">
                                    {configs.length > 1 && (
                                        <div className="source-filter">
                                            <label>Filtrer par source :</label>
                                            <select value={sourceFilter || ''} onChange={(e) => setSourceFilter(e.target.value ? parseInt(e.target.value) : null)}>
                                                <option value="">Tous les annuaires</option>
                                                {configs.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({getDirectoryTypeLabel(c.directory_type)})</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="tab-info">
                                        <p>Les signatures assignées directement à un utilisateur sont prioritaires sur les signatures de groupe. Cliquez sur un utilisateur pour gérer ses signatures.</p>
                                    </div>
                                    {users.length === 0 ? (
                                        <div className="empty-state">
                                            <p>Aucun employé synchronisé</p>
                                            <p className="hint">Configurez l'annuaire et lancez une synchronisation</p>
                                        </div>
                                    ) : (
                                        <div className="users-list">
                                            {users.map(u => (
                                                <div key={u.id} className={`user-card ${!u.is_active ? 'inactive' : ''} ${expandedUserId === u.id ? 'expanded' : ''}`}>
                                                    <div className="user-header" onClick={() => toggleUserExpand(u.id)}>
                                                        <div className="user-info">
                                                            <span className="user-name">
                                                                {u.first_name} {u.last_name}
                                                                {u.is_shared_mailbox === 1 && (
                                                                    <span className="shared-mailbox-badge">Boîte partagée</span>
                                                                )}
                                                                {u.config_name && <span className="source-badge">{u.config_name}</span>}
                                                                {!u.directory_config_id && <span className="source-badge orphan">Non rattaché</span>}
                                                            </span>
                                                            <span className="user-email">{u.email}</span>
                                                        </div>
                                                        <div className="user-meta">
                                                            <span className="user-groups">{u.group_names || 'Aucun groupe'}</span>
                                                            <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                                                                {u.is_active ? 'Actif' : 'Inactif'}
                                                            </span>
                                                            {u.assigned_signatures?.length > 0 && (
                                                                <span className="user-signature-count" title="Signatures individuelles assignées">
                                                                    {u.assigned_signatures.length} sig.
                                                                </span>
                                                            )}
                                                            <span className="expand-icon">{expandedUserId === u.id ? '▼' : '▶'}</span>
                                                        </div>
                                                    </div>
                                                    {expandedUserId === u.id && (
                                                        <div className="user-details">
                                                            <div className="user-signatures">
                                                                <h5>Signatures individuelles:</h5>
                                                                {u.assigned_signatures?.length > 0 ? (
                                                                    <ul className="signature-list">
                                                                        {u.assigned_signatures.map(sig => (
                                                                            <li key={sig.id}>
                                                                                <span className="sig-name">{sig.name}</span>
                                                                                <span className="sig-priority-badge">Priorité: 200</span>
                                                                                <button
                                                                                    className="btn-remove"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveSignatureFromUser(u.id, sig.id);
                                                                                    }}
                                                                                    title="Retirer"
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : (
                                                                    <p className="no-signatures">Aucune signature individuelle</p>
                                                                )}
                                                                <button
                                                                    className="btn-add-signature"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setUserAssignModal({
                                                                            open: true,
                                                                            userId: u.id,
                                                                            userName: `${u.first_name} ${u.last_name}`
                                                                        });
                                                                    }}
                                                                >
                                                                    + Assigner une signature
                                                                </button>
                                                            </div>
                                                            <div className="user-extra-info">
                                                                <p><strong>Dernière sync:</strong> {formatDate(u.last_synced_at)}</p>
                                                                {u.department && <p><strong>Département:</strong> {u.department}</p>}
                                                                {u.job_title && <p><strong>Poste:</strong> {u.job_title}</p>}
                                                                <div className="user-action-buttons">
                                                                    <button
                                                                        className="btn-personalization"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openPersonalizationModal(u);
                                                                        }}
                                                                        title="Personnaliser (formule de politesse, photo)"
                                                                    >
                                                                        👤 Personnaliser
                                                                    </button>
                                                                    <button
                                                                        className="btn-preview-signature"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openPreviewModal(u);
                                                                        }}
                                                                    >
                                                                        Voir la signature effective
                                                                    </button>
                                                                    <button
                                                                        className="btn-calendar"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/signatures/calendar?user=${u.id}`);
                                                                        }}
                                                                        title="Voir le calendrier des signatures"
                                                                    >
                                                                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                        </svg>
                                                                        Calendrier
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Shared Mailboxes Tab */}
                            {activeTab === 'shared' && (
                                <SharedMailboxesTab
                                    users={users}
                                    signatures={signatures}
                                    onRefresh={loadData}
                                />
                            )}

                            {/* Logs Tab */}
                            {activeTab === 'logs' && (
                                <div className="logs-tab">
                                    {configs.length > 1 && (
                                        <div className="source-filter">
                                            <label>Filtrer par source :</label>
                                            <select value={sourceFilter || ''} onChange={(e) => setSourceFilter(e.target.value ? parseInt(e.target.value) : null)}>
                                                <option value="">Tous les annuaires</option>
                                                {configs.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({getDirectoryTypeLabel(c.directory_type)})</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {logs.length === 0 ? (
                                        <div className="empty-state">
                                            <p>Aucun historique de synchronisation</p>
                                        </div>
                                    ) : (
                                        <table className="logs-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Source</th>
                                                    <th>Type</th>
                                                    <th>Statut</th>
                                                    <th>Utilisateurs</th>
                                                    <th>Groupes</th>
                                                    <th>Erreur</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logs.map(log => (
                                                    <tr key={log.id}>
                                                        <td>{formatDate(log.started_at)}</td>
                                                        <td>{log.config_name || <span style={{color:'#9ca3af'}}>-</span>}</td>
                                                        <td>{log.sync_type === 'manual' ? 'Manuelle' : 'Automatique'}</td>
                                                        <td>
                                                            <span className={`status-badge ${log.status}`}>
                                                                {log.status === 'success' ? 'Succès' :
                                                                 log.status === 'failed' ? 'Échec' :
                                                                 log.status === 'partial' ? 'Partiel' : log.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="stat-created">+{log.users_created}</span>
                                                            <span className="stat-updated">↻{log.users_updated}</span>
                                                            {log.users_failed > 0 && <span className="stat-failed">✕{log.users_failed}</span>}
                                                        </td>
                                                        <td>
                                                            <span className="stat-created">+{log.groups_created}</span>
                                                            <span className="stat-updated">↻{log.groups_updated}</span>
                                                            {log.groups_failed > 0 && <span className="stat-failed">✕{log.groups_failed}</span>}
                                                        </td>
                                                        <td className="error-cell">{log.error_message || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Assign Signature to Group Modal */}
            {assignModal.open && (
                <div className="modal-overlay" onClick={() => setAssignModal({ open: false, groupId: null, groupName: '' })}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Assigner une signature au groupe</h3>
                            <button className="close-btn" onClick={() => setAssignModal({ open: false, groupId: null, groupName: '' })}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>Groupe: <strong>{assignModal.groupName}</strong></p>
                            <div className="form-group">
                                <label>Sélectionner une signature</label>
                                <select
                                    value={selectedSignature}
                                    onChange={(e) => setSelectedSignature(e.target.value)}
                                >
                                    <option value="">-- Choisir --</option>
                                    {signatures.map(sig => (
                                        <option key={sig.id} value={sig.id}>{sig.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setAssignModal({ open: false, groupId: null, groupName: '' })}>
                                Annuler
                            </button>
                            <button className="btn-primary" onClick={handleAssignSignature} disabled={!selectedSignature}>
                                Assigner
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Signature to User Modal */}
            {userAssignModal.open && (
                <div className="modal-overlay" onClick={() => setUserAssignModal({ open: false, userId: null, userName: '' })}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Assigner une signature individuelle</h3>
                            <button className="close-btn" onClick={() => setUserAssignModal({ open: false, userId: null, userName: '' })}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>Utilisateur: <strong>{userAssignModal.userName}</strong></p>
                            <div className="info-note">
                                Les signatures individuelles ont une priorité de 200, supérieure aux signatures de groupe (1-100).
                            </div>
                            <div className="form-group">
                                <label>Sélectionner une signature</label>
                                <select
                                    value={selectedUserSignature}
                                    onChange={(e) => setSelectedUserSignature(e.target.value)}
                                >
                                    <option value="">-- Choisir --</option>
                                    {signatures.map(sig => (
                                        <option key={sig.id} value={sig.id}>{sig.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setUserAssignModal({ open: false, userId: null, userName: '' })}>
                                Annuler
                            </button>
                            <button className="btn-primary" onClick={handleAssignSignatureToUser} disabled={!selectedUserSignature}>
                                Assigner
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Effective Signature Modal */}
            {previewModal.open && (
                <div className="modal-overlay" onClick={closePreviewModal}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Signature effective</h3>
                            <button className="close-btn" onClick={closePreviewModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="preview-user-info">
                                <span className="preview-user-name">
                                    {previewModal.user?.first_name} {previewModal.user?.last_name}
                                </span>
                                <span className="preview-user-email">{previewModal.user?.email}</span>
                            </div>

                            <div className="preview-date-picker">
                                <label>Date de simulation:</label>
                                <input
                                    type="date"
                                    value={previewModal.selectedDate}
                                    onChange={(e) => handlePreviewDateChange(e.target.value)}
                                />
                            </div>

                            <div className="preview-algorithm-info">
                                <strong>Algorithme de priorité:</strong> Utilisateur (200) &gt; Groupe (1-100) &gt; Global (0)
                            </div>

                            {previewModal.loading ? (
                                <div className="preview-loading">Chargement...</div>
                            ) : previewModal.effectiveSignatures.length === 0 ? (
                                <div className="preview-empty">
                                    <p>Aucune signature applicable pour cet utilisateur à cette date.</p>
                                </div>
                            ) : (
                                <div className="preview-signatures-list">
                                    {previewModal.effectiveSignatures.map((sig, index) => (
                                        <div
                                            key={sig.id}
                                            className={`preview-signature-item ${index === 0 ? 'winner' : ''}`}
                                        >
                                            <div className="preview-sig-rank">
                                                {index === 0 ? (
                                                    <span className="rank-badge winner">ACTIVE</span>
                                                ) : (
                                                    <span className="rank-badge">#{index + 1}</span>
                                                )}
                                            </div>
                                            <div className="preview-sig-info">
                                                <span className="preview-sig-name">{sig.name}</span>
                                                <div className="preview-sig-meta">
                                                    <span className={`assignment-type ${sig.assignment_type}`}>
                                                        {sig.assignment_type === 'user' ? 'Utilisateur' :
                                                         sig.assignment_type === 'group' ? 'Groupe' :
                                                         sig.assignment_type === 'default' ? 'Par défaut' : 'Global'}
                                                    </span>
                                                    <span className="priority-value">
                                                        Priorité: {sig.assignment_priority}
                                                    </span>
                                                    {sig.valid_from && (
                                                        <span className="validity">
                                                            Du {new Date(sig.valid_from).toLocaleDateString('fr-FR')}
                                                            {sig.valid_until && ` au ${new Date(sig.valid_until).toLocaleDateString('fr-FR')}`}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closePreviewModal}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Personalization Modal */}
            {personalizationModal.open && (
                <div className="modal-overlay" onClick={closePersonalizationModal}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Personnalisation</h3>
                            <button className="close-btn" onClick={closePersonalizationModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ marginBottom: '20px' }}>
                                <strong>{personalizationModal.user?.first_name} {personalizationModal.user?.last_name}</strong>
                                <br />
                                <span style={{ color: '#6b7280', fontSize: '14px' }}>{personalizationModal.user?.email}</span>
                            </p>

                            {/* Photo de profil */}
                            <div className="form-group">
                                <label>Photo de profil</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: '#f3f4f6',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #e5e7eb'
                                    }}>
                                        {personalizationModal.profilePhoto ? (
                                            <img
                                                src={personalizationModal.profilePhoto.startsWith('http')
                                                    ? personalizationModal.profilePhoto
                                                    : appConfig.getUploadUrl(personalizationModal.profilePhoto)}
                                                alt="Photo de profil"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <span style={{ fontSize: '32px', color: '#9ca3af' }}>👤</span>
                                        )}
                                    </div>
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleProfilePhotoSelect}
                                            style={{ display: 'none' }}
                                            id="profile-photo-input"
                                        />
                                        <label
                                            htmlFor="profile-photo-input"
                                            style={{
                                                padding: '8px 16px',
                                                background: '#4f46e5',
                                                color: 'white',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                display: 'inline-block'
                                            }}
                                        >
                                            {personalizationModal.uploading ? 'Upload...' : 'Changer la photo'}
                                        </label>
                                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                            JPG, PNG, GIF ou WebP. Max 5 Mo.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Formule de politesse */}
                            <div className="form-group" style={{ marginTop: '20px' }}>
                                <label>Formule de politesse</label>
                                <textarea
                                    value={personalizationModal.greeting}
                                    onChange={(e) => setPersonalizationModal(prev => ({ ...prev, greeting: e.target.value }))}
                                    placeholder="Ex: Cordialement, / Bien à vous, / Meilleures salutations,"
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        resize: 'vertical'
                                    }}
                                />
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                    Sera ajoutée automatiquement avant la signature. Utilisez le shortcode <code>{'{greeting}'}</code> dans vos modèles.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closePersonalizationModal}>
                                Annuler
                            </button>
                            <button className="btn-primary" onClick={handleSavePersonalization}>
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Cropper Modal */}
            {cropperModal.open && cropperModal.imageSrc && (
                <ImageCropper
                    image={cropperModal.imageSrc}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    aspectRatio={1}
                    outputFormat={cropperModal.imageType}
                />
            )}
        </>
    );
}

export default DirectorySettingsPage;
